import { AfterViewInit, Component, Inject, Injector, OnDestroy, OnInit } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatOptionSelectionChange } from "@angular/material";
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from "rxjs";
import { filter, map, take } from "rxjs/operators";

import { DialogSearchUserComponent } from "../dialog-search-user/dialog-search-user.component";
import { Logger } from "../../../model/logger.model";
import { LoggingService } from "../../../service/logging.service";
import { Order } from "../../../persistence/view-dto/order.model";
import { OrderEventService } from "../../../service/order-event.service";
import { OrderService } from "../../../service/order.service";
import { OrderEventEngine, OrderEventEngineBuilder } from "../../../model/view/order-event-engine.model";
import { UserFind } from "../../../persistence/dto/user-find.model";
import { AdvancedForm, AdvancedFormBuilder } from "../../../model/advanced-form.model";
import { OrderEventForm } from "../../../model/form/order-event.form";
import { OrderEventOriginType, toOrderEventOriginTypeString } from "../../../persistence/dto/order-event-origin-type.enum";
import { Enum } from "../../../support/enum.support";
import { OrderEventDialogPayload } from "../../../interface/type/order-event-dialog-payload.type";
import { OrderEventDialogResultPayload } from "../../../interface/type/order-event-dialog-result-payload.type";
import { andSpread } from "../../../model/operator/and-spread.operator";
import { OrderEventStatus, toOrderEventStatusString } from "../../../persistence/dto/order-event-status.enum";
import { Unsubscriber } from "../../../support/unsubscriber.support";
import { orElse } from "../../../model/operator/or-else.operator";
import { OrderEventTypeId, toOrderEventTypeIdString } from "../../../model/view/order-event-type-id.enum";
import { OrderItem } from "../../../persistence/view-dto/order-item.model";
import { CampaignProduct } from "../../../persistence/dto/campaign-product.model";
import { OrderEventType } from "../../../persistence/dto/order-event-type.model";
import { UIService } from "../../../service/ui.service";
import { flatMap } from "../../../model/operator/flat-map.operator";
import { TypeSystem } from "../../../support/type-system.support";
import { AuthenticationService } from "../../../service/authentication.service";
import { BackendPermission } from "../../../model/backend-permission.enum";
import { UserService } from "../../../service/user.service";
import { ResellerStatus, resellerStatusToString } from "../../../persistence/dto/reseller-status.enum";

@Component({
	selector: "kt-dialog-product",
	templateUrl: "./dialog-order-event.component.html",
	styleUrls: ["../../../ui/style/main.scss", "./dialog-order-event.component.scss"]
})
export class DialogOrderEventComponent implements AfterViewInit, OnDestroy, OnInit {

	/**
	 * @description
	 *  Logger principal de este componente.
	 */
	private logger: Logger = this.loggingService.getLogger("dialog-order-event.component");

	/**
	 * @description
	 *	Handler de subscripciones.
	 */
	private unsubscriber: Unsubscriber;

	/**
	 * @description
	 *	Formulario para crear la nota.
	 */
	public advancedForm: AdvancedForm;

	// Columnas para cada tabla:
	public columnsForItem: string[] = [
		"code", "image", "name", "brochurePrice", "invoicePrice",
		"quantity", "totalBrochurePrice", "totalInvoicePrice", "actions"
	];
	public columnsForProduct: string[] = [
		"code", "image", "name", "brochurePrice", "quantity",
		"totalBrochurePrice", "actions"
	];
	public columnsForReplacement: string[] = [
		"code", "image", "name", "brochurePrice", "quantity",
		"totalBrochurePrice", "actions"
	];
	public columnsForTracking: string[] = [
		"timestamp", "applicationUserName", "orderEventStatus", "descriptionStatus"
	];

	/**
	 * @description
	 *	Motor principal de notas de CyR/NC.
	 */
	public orderEventEngine: OrderEventEngine = undefined;

	// Observables disponibles para la UI:
	public canAddReplacementItem = new BehaviorSubject<boolean>(false);
	public canOverrideCreditNoteValidations = new BehaviorSubject<boolean>(false);
	public inCreationMode = new BehaviorSubject<boolean>(false);
	public inDetailMode = new BehaviorSubject<boolean>(false);
	public inLoadingMode = new BehaviorSubject<boolean>(true);
	public isChangeOverWrongProductShadeFraganceOrSize = new BehaviorSubject<boolean>(false);
	public isCreditNote = new BehaviorSubject<boolean>(false);
	public isCreditNoteOverCycles = new BehaviorSubject<boolean>(false);
	public isDisarm = new BehaviorSubject<boolean>(false);
	public isUnderOriginalAmount = new BehaviorSubject<boolean>(false);
	public filteredCampaignProducts = new BehaviorSubject<CampaignProduct[]>([]);
	public filteredReplacementItems = new BehaviorSubject<CampaignProduct[]>([]);
	public filteredOrderItems = new BehaviorSubject<OrderItem[]>([]);
	public orderEventOriginTypes = new BehaviorSubject<OrderEventOriginType[]>([]);
	public orders = new BehaviorSubject<Order[]>([]);
	public reseller = new BehaviorSubject<string>("");
	public hasReplacementItems = new BehaviorSubject<boolean>(false);

	// Ítems seleccionados para agregar (auto-complete):
	public campaignProductToAdd: CampaignProduct = undefined;
	public orderItemToAdd: OrderItem = undefined;
	public replacementItemToAdd: CampaignProduct = undefined;

	// Productos para filtrar y códigos ingresados:
	public campaignProducts: CampaignProduct[] = [];
	public orderItems: OrderItem[] = [];
	public campaignProductInput: string = "";
	public orderItemInput: string = "";
	public replacementItemInput: string = "";

	constructor(
		@Inject(MAT_DIALOG_DATA) public payload: OrderEventDialogPayload,
		private dialog: MatDialog,
		private dialogRef: MatDialogRef<DialogOrderEventComponent, OrderEventDialogResultPayload>,
		private injector: Injector,
		private authenticationService: AuthenticationService,
		private loggingService: LoggingService,
		private orderEventService: OrderEventService,
		private orderService: OrderService,
		private uiService: UIService,
		private userService: UserService) {}

	public ngOnDestroy(): void {
		this.canAddReplacementItem.complete();
		this.canOverrideCreditNoteValidations.complete();
		this.isChangeOverWrongProductShadeFraganceOrSize.complete();
		this.inCreationMode.complete();
		this.isCreditNoteOverCycles.complete();
		this.inDetailMode.complete();
		this.inLoadingMode.complete();
		this.isDisarm.complete();
		this.isCreditNote.complete();
		this.isUnderOriginalAmount.complete();
		this.filteredCampaignProducts.complete();
		this.filteredOrderItems.complete();
		this.filteredReplacementItems.complete();
		this.orderEventOriginTypes.complete();
		this.orders.complete();
		this.reseller.complete();
		this.orderEventEngine.release();
		this.unsubscriber.releaseSubscriptions();
		this.hasReplacementItems.complete();
		
	}

	public ngOnInit(): void {
		this.logger.debug("Initializing with payload:", this.payload);
		this.advancedForm = new AdvancedFormBuilder()
			.logger(this.loggingService.getLogger("dialog-order-event.form"))
			.specification(OrderEventForm)
			.build()
			.disable();
		this.orderEventEngine = new OrderEventEngineBuilder()
			.advancedForm(this.advancedForm)
			.injector(this.injector)
			.logger(this.loggingService.getLogger("dialog-order-event.engine"))
			.build();
		this.unsubscriber = new Unsubscriber()
			.addSubscription(this.inCreationMode
				.subscribe({
					next: inCreationMode => {
						if (inCreationMode) {
							this.orderEventOriginTypes.next([ OrderEventOriginType.ZoneManager ]);
							this.loadCreationMode();
						}
					},
					error: (error: Error) => this.logger.error(error)
				}))
			.addSubscription(this.inDetailMode
				.subscribe({
					next: inDetailMode => {
						if (inDetailMode) {
							this.orderEventOriginTypes.next(Enum.valuesOf(OrderEventOriginType, "string"));
							this.loadDetailMode(this.payload.id);
						}
					},
					error: (error: Error) => this.logger.error(error)
				}))
			.addSubscription(this.userService.currentUser()
				.subscribe({
					next: user => {
						const isGigotEmployee = user.resellerStatus === resellerStatusToString(ResellerStatus.GigotEmployee);
						this.canOverrideCreditNoteValidations.next(this.authenticationService
							.hasPermission(BackendPermission.CanOverrideCreditNoteValidations));
					},
					error: (error: Error) => this.logger.error(error)
				}));
		if (this.payload.id === undefined) {
			this.inCreationMode.next(true);
		}
		else {
			this.inDetailMode.next(true);
		}
	}

	public ngAfterViewInit(): void {
		this.unsubscriber
			.addSubscription(combineLatest([
				this.orderEventEngine.totalOrderEventBrochurePrice(),
				this.orderEventEngine.totalReplacementItemsBrochurePrice()
			])
			.subscribe({
				next: ([totalOrderEventBrochurePrice, totalReplacementItemsBrochurePrice]) => {
					this.isUnderOriginalAmount.next(totalReplacementItemsBrochurePrice < totalOrderEventBrochurePrice);
				},
				error: (error: Error) => this.logger.error(error)
			}))
			.addSubscription(this.advancedForm.netChangeIfPresent("generateCreditNote")
				.subscribe((generateCreditNote: boolean) => this.isCreditNote.next(generateCreditNote)))
			.addSubscription(this.advancedForm.netChangeIfPresent("orderEventTypeId")
				.subscribe((orderEventTypeId: number) => {
					this.isDisarm.next(orderEventTypeId === OrderEventTypeId.Disarm);
					this.isCreditNoteOverCycles
						.next(orderEventTypeId === OrderEventTypeId.WaiverToProductsPurchasedWithPaymentCycles);
					this.isChangeOverWrongProductShadeFraganceOrSize
						.next(orderEventTypeId === OrderEventTypeId.WrongProductShadeFraganceOrSize);
					this.clearComponentState();
				}))
			.addSubscription(this.advancedForm.netChangeOver("orderEventTypeId")
				.pipe(map(netChange => !this.orderEventEngine
					.causesWithAutoInsertion()
					.has(netChange.delta["orderEventTypeId"])
				))
				.subscribe(can => this.canAddReplacementItem.next(can)))
			.addSubscription(this.orderEventEngine.availableOrderItems()
				.subscribe(orderItems => {
					this.orderItems = orderItems;
					const filteredOrderItems = orderItems
						.filter(orderItem => new RegExp(this.orderItemInput, "gi")
							.test(`${orderItem.productCode} ${orderItem.productName}`));
					this.filteredOrderItems.next(filteredOrderItems);
				}))
			.addSubscription(this.orderEventEngine.availableCampaignProducts()
				.subscribe(campaignProducts => {
					this.campaignProducts = campaignProducts;
					const filteredCampaignProducts = campaignProducts
						.filter(campaignProduct => new RegExp(this.campaignProductInput, "gi")
							.test(`${campaignProduct.product.code} ${campaignProduct.product.name}`));
					const filteredReplacementItems = campaignProducts
						.filter(campaignProduct => new RegExp(this.replacementItemInput, "gi")
							.test(`${campaignProduct.product.code} ${campaignProduct.product.name}`));
					this.hasReplacementItems.next(filteredReplacementItems.length > 0);
					this.filteredCampaignProducts.next(filteredCampaignProducts);
					this.filteredReplacementItems.next(filteredReplacementItems);
				}));
	}

	/**
	 * @description
	 *	Filtra las causas disponibles para la vista. Si es modo detalle, se
	 *	pueden ver todas las causas. Si es modo creación, se pueden ver todas
	 *	excepto el tipo 'Desarme' (11) y 'Producto en cuotas cortado en
	 *	facturación' (12). Si el tipo es CyR, no se puede ver la causa
	 *	'Renuncia de productos comprados en cuotas' (9).
	 *
	 * @returns
	 *	La lista de causas disponibles para la vista.
	 */
	public filteredOrderEventTypes(): Observable<OrderEventType[]> {
		return this.orderEventEngine.availableOrderEventTypes()
			.pipe(
				map(orderEventTypes => orderEventTypes
					.filter(orderEventType => this.inDetailMode.value
						|| (orderEventType.id !== OrderEventTypeId.Disarm
							&& orderEventType.id !== OrderEventTypeId.ProductPurchasedWithPaymentCyclesWithoutStock
							&& (this.isCreditNote.value
								|| orderEventType.id !== OrderEventTypeId.WaiverToProductsPurchasedWithPaymentCycles))
					)));
	}

	/**
	 * @description
	 *	Evento lanzado al seleccionar un nuevo ítem para la nota. El código
	 *	asociado del producto se envía al motor de notas para que recalcule
	 *	los montos y productos disponibles en cada filtro asociado.
	 *
	 * @param event
	 *	El evento de selección del producto.
	 * @param orderEventItem
	 *	El modelo del ítem seleccionado del cual extraer el código.
	 */
	public onAutocompleteItem(event: MatOptionSelectionChange, type: string, item: any): void {
		if (event.isUserInput) {
			this.logger.debug("onAutocompleteItem:", event, type, item);
			switch (type) {
				case "campaignProduct":
					this.campaignProductToAdd = item as CampaignProduct;
					break;
				case "orderItem":
					this.orderItemToAdd = item as OrderItem;
					break;
				case "replacementItem":
					this.replacementItemToAdd = item as CampaignProduct;
					break;
				default:
					this.logger.fatal("Unknown autocomplete-item type:", type);
			}
		}
	}

	/**
	 * @description
	 *	Evento lanzado al escribir en el selector de ítems del pedido actual.
	 *	Se espera que se utilice el código ingresado para filtrar la lista
	 *	disponible de productos.
	 *
	 * @param event
	 *	El evento asociado, que posee el caracter ingresado.
	 */
	public onAutocompleteItemKeyUp(event: KeyboardEvent, type: string): void {
		this.logger.debug("onAutocompleteItemKeyUp:", event.key, type);
		const inputValue: string = (event.target as HTMLInputElement).value
			.replace(/[^ a-zA-Z0-9]|^\s+|\s+$/gi, "")
			.replace(/\s+/gi, " ");
		switch (type) {
			case "campaignProduct": {
				this.campaignProductInput = inputValue;
				const filteredCampaignProducts = this.campaignProducts
					.filter(campaignProduct => new RegExp(this.campaignProductInput, "gi")
						.test(`${campaignProduct.product.code} ${campaignProduct.product.name}`));
				this.filteredCampaignProducts.next(filteredCampaignProducts);
				break;
			}
			case "orderItem": {
				this.orderItemInput = inputValue;
				const filteredOrderItems = this.orderItems
					.filter(orderItem => new RegExp(this.orderItemInput, "gi")
						.test(`${orderItem.productCode} ${orderItem.productName}`));
				this.filteredOrderItems.next(filteredOrderItems);
				break;
			}
			case "replacementItem": {
				this.replacementItemInput = inputValue;
				const filteredReplacementItems = this.campaignProducts
					.filter(campaignProduct => new RegExp(this.replacementItemInput, "gi")
						.test(`${campaignProduct.product.code} ${campaignProduct.product.name}`));
				this.filteredReplacementItems.next(filteredReplacementItems);
				
				break;
			}
			default:
				this.logger.fatal("Unknown autocomplete-item key-up type:", type);
		}
	}

	/**
	 * @description
	 *	Cierra el modal, sin error. Se ejecuta al cancelar la creación o
	 *	edición de la nota.
	 */
	public onCancel(): void {
		this.logger.debug("Canceling...");
		this.dialogRef.close({
			ok: true,
			orderEvent: undefined
		});
	}

	/**
	 * @description
	 *	Limpieza del usuario seleccionado. Ejecuta el vaciado total de la nota
	 *	a crear, ya que se modifica el usuario.
	 */
	public onClearReseller(): void {
		this.orders.next([]);
		this.reseller.next("");
		this.advancedForm.patch({ "applicationUserId": undefined });
	}

	/**
	 * @description
	 *	Último proceso ejecutado en el modal al emitir la creación final de la
	 *	nota deseada. El motor es quién indica cuándo la nota se encuentra en
	 *	un estado válido para creación.
	 */
	public onCreate(): void {
		TypeSystem.asyncFrom(true).pipe(
			flatMap(_ => this.orderEventEngine.controlStatus()), take(1),
			flatMap(controlStatus => {
				// Esta condición es una regla de negocio, debería moverse
				// dentro del observable 'controlStatus'.
				const orderEventTypeId = this.advancedForm.valueOf("orderEventTypeId");
				if (controlStatus
					&& (orderEventTypeId === OrderEventTypeId.ProductInvoicedDoesNotMatchProductShipped
					|| orderEventTypeId === OrderEventTypeId.ProductInvoicedAndNotShipped)) {
					return this.uiService.confirmationDialog("Se requiere confirmación",
						`El revendedor ya posee al menos 2 CyR/NC durante las últimas 10
						campañas, por lo cual, si crea una nueva solicitud se aplicará
						un "Control de Caja". Para crear la solicitud de todas formas
						presione OK; para cancelar, presione CERRAR.`);
				}
				else {
					this.logger.debug("The reseller doesn't required package-control.");
					return of(true);
				}
			}),
			filter(ok => ok),
			flatMap(_ => {
				if (!this.isCreditNote.value && this.isUnderOriginalAmount.value) {
					return this.uiService.confirmationDialog("Se requiere confirmación",
						`El monto total de los productos de reemplazo es inferior al
						monto de los productos a devolver. No se le devolverá la
						diferencia como nota de crédito (NC). Para crear la solicitud de
						cambio de todos modos presione OK; si desea cancelar presione
						CERRAR.`);
				}
				else return of(true);
			}),
			filter(ok => ok)
		)
		.subscribe({
			next: _ => {
				this.logger.debug("Creating note...");
				this.inLoadingMode.next(true);
				const orderEventForCreation = this.orderEventEngine.loadOrderEventForCreation();
				this.logger.debug("Sending:", orderEventForCreation);
				this.orderEventService
					.createOrderEvent(orderEventForCreation)
					.subscribe({
						next: orderEvent => {
							this.logger.debug("Created order event:", orderEvent);
							this.inLoadingMode.next(false);
							this.dialogRef.close({ ok: true, orderEvent: orderEvent });
						},
						error: (error: HttpErrorResponse) => {
							// Estandarizar error-handling:
							this.logger.error("Error while creating order event:", error);
							if (Array.isArray(error.error)) {
								const allErrors: string = (error.error as any[])
									.map(error => error.message)
									.reduce((prefix, message) => `${prefix} ${message}`, "");
								this.uiService.errorNotification(allErrors);
							}
							else {
								this.uiService.errorNotification(
									"Se produjo un error desconocido en el servidor principal. Contacte a sistemas.");
							}
							this.inLoadingMode.next(false);
						}
					});
			},
			error: (error: Error) => this.logger.error(error)
		});
	}

	/**
	 * @description
	 *	Ejecuta el proceso de búsqueda de un usuario/revendedor. Al obtener el
	 *	usuario, descarga sus pedidos disponibles para crear notas y actualiza
	 *	el formulario, lo que propaga los cambios hacia el motor de notas.
	 */
	public onFindReseller(): void {
		const dialogData = {
			width: "75%",
			data: {
				userId: undefined,
				userName: undefined
			}
		};
		this.inLoadingMode.next(true);
		this.dialog.open(DialogSearchUserComponent, dialogData)
			.afterClosed()
			.pipe(andSpread((reseller: UserFind) => ({
				reseller: of(reseller),
				fromOrders: this.orderService.tryOrdersForOrderEvent(reseller)
					.pipe(andSpread(_ => ({
						orders: of(_),
						orderViews: forkJoin(_.map(order => this.orderService.orderByID(order.id)))
							.pipe(orElse([] as Order[]))
					})))
			})))
			.subscribe({
				next: _ => {
					if (_.reseller !== undefined) {
						this.advancedForm.patch({ "applicationUserId": _.reseller.id });
						this.reseller.next(`${_.reseller.lastName}, ${_.reseller.firstName} (${_.reseller.userName})`);
						this.orders.next(_.fromOrders.orderViews.sort((o1, o2) => o2.campaignId - o1.campaignId));
						if (0 == _.fromOrders.orderViews.length) {
							this.uiService.warningNotification("El revendedor seleccionado no posee pedidos disponibles para realizar una nota.");
						}
					}
					this.inLoadingMode.next(false);
				},
				error: (error: Error) => {
					let message = "Ocurrió un error inesperado. Contacte a sistemas.";
					if (error instanceof HttpErrorResponse) {
						const status = (error as HttpErrorResponse).status;
						if (status == 400) {
							message = "No tiene permisos para acceder a los pedidos de este revendedor.";
						}
					}
					this.uiService.errorNotification(message);
					this.logger.error(error);
					this.inLoadingMode.next(false);
				}
			});
	}

	/**
	 * @description
	 *	Convierte a texto el tipo de origen de la nota, para visualización.
	 */
	public toOrderEventOriginTypeString(orderEventOriginType: OrderEventOriginType): string {
		return toOrderEventOriginTypeString(orderEventOriginType);
	}

	/**
	 * @description
	 *	Convierte a texto el estado de la nota, para visualización.
	 */
	public toOrderEventStatusString(orderEventStatus: OrderEventStatus): string {
		return toOrderEventStatusString(orderEventStatus);
	}

	/**
	 * @description
	 *	Convierte a texto la causa de la nota, para visualización.
	 */
	public toOrderEventTypeIdString(orderEventTypeId: OrderEventTypeId): string {
		return toOrderEventTypeIdString(orderEventTypeId);
	}

	/**
	 * @description
	 *	Reinicia el estado de la vista a un punto inicial conocido para evitar
	 *	efectos colaterales al modificar los modelos u el motor asociados.
	 */
	private clearComponentState(): void {
		this.campaignProductToAdd = undefined;
		this.orderItemToAdd = undefined;
		this.replacementItemToAdd = undefined;
		this.campaignProductInput = "";
		this.orderItemInput = "";
		this.replacementItemInput = "";
	}

	/**
	 * @description
	 *	Prepara la vista para el modo de creación y ejecuta el motor de notas.
	 */
	private loadCreationMode(): void {
		this.logger.debug("Creation mode...");
		this.advancedForm.enable();
		this.advancedForm.patch({ "generateCreditNote": this.payload.generateCreditNote });
		this.isCreditNote.next(this.payload.generateCreditNote);
		this.inLoadingMode.next(false);
	}

	/**
	 * @description
	 *	Descarga el detalle y los modelos asociados a la nota, y ejecuta el
	 *	motor con dicha información.
	 */
	private loadDetailMode(orderEventId: number): void {
		this.logger.debug("Detail mode...");
		this.unsubscriber.addSubscription(this.orderEventEngine
			.forOrderEvent(orderEventId)
			.order()
			.pipe(
				filter(optionalOrder => optionalOrder.isPresent()),
				map(optionalOrder => optionalOrder.value)
			)
			.subscribe({
				next: order => {
					const applicationUser = order.applicationUser;
					this.orders.next([ order ]);
					this.reseller.next(`${applicationUser.lastName}, ${applicationUser.firstName} (${applicationUser.userName})`);
					this.inLoadingMode.next(false);
				},
				error: (error: Error) => {
					this.logger.error(error);
					this.inLoadingMode.next(false);
				}
			}));
	}
}
